import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import i18next from 'i18next';

import * as SC from '~src/shared/sidebar/styles/subnav.sc';
import { SubNavContainer } from '~src/shared/sidebar/styles/sidebar.sc';
import { SideBarItemI, SideBarSection } from '~src/shared/sidebar/constants/sideBarData';
import { CycleI } from '~src/interfaces/cycle.interface';

import KeyResultSubNav from './KeyResultSubNav';
import SvgIcon from '~src/shared/SvgIcon';

interface Props {
  menuItem: SideBarItemI;
  cycles: CycleI[];
  defaultUrl: string;
  offset?: number | null;
  hideMenuIfEmpty?: boolean;
}

const ListItem: FC<{ item: SideBarSection; t: i18next.TFunction }> = ({ item, t }) => {
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (item.url && item.url.indexOf('http') === 0) {
      e.preventDefault();
      e.stopPropagation();
      document.location.assign(item.url);
    }
  };


  if (item.url && item.icon) {
    const iconWidth = item.icon === 'iconEditTopicsQuestions' ? 24 : 20;
    return (
      <SC.SubNavItem>
        <SC.NavItemWithIcon $isItemScales={item.icon === 'iconManageScales'} to={item.url} key={item.link} onClick={onClick}>
          <SvgIcon name={item.icon} width={iconWidth} height={iconWidth} viewBoxWidth={24} viewBoxHeight={24} />
          <SC.ItemText>{t(item.link)}</SC.ItemText>
        </SC.NavItemWithIcon>
      </SC.SubNavItem>
    );
  }

  return (
    <SC.SubNavItem>
      {item.url ? (
        <SC.StyledSideBarLink to={item.url} key={item.link} onClick={onClick}>
          <SC.SubNavItemLinkText>{t(item.link)}</SC.SubNavItemLinkText>
        </SC.StyledSideBarLink>
      ) : (
        <SC.SubNavItemLinkText>{t(item.link)}</SC.SubNavItemLinkText>
      )}
    </SC.SubNavItem>
  );
};

const ListSubNav: FC<Props> = ({ menuItem: { title, name, sectionLinks }, defaultUrl, offset, hideMenuIfEmpty }) => {
  const [t] = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const hasSections = typeof sectionLinks === 'function' ? sectionLinks()?.some((item) => item.items.length >= 1) : sectionLinks?.some((item) => item.items.length >= 1)

  const repositionEl = useCallback(
    debounce(() => {
      if (!containerRef.current) return;

      const computedStyle = window.getComputedStyle(containerRef.current);

      if (computedStyle.display === 'none') return;

      const el = containerRef.current;
      const boundingRect = el.getBoundingClientRect();

      const spaceBelow = window.innerHeight - Math.ceil(boundingRect.bottom);
      const isLowerThanParent =
        el.parentElement && el.parentElement.getBoundingClientRect().top - boundingRect.top < 0;
      const isHigherThanParent =
        el.parentElement && el.parentElement.getBoundingClientRect().bottom - boundingRect.bottom > 0;

      if (spaceBelow <= 0 && !isHigherThanParent) {
        el.style.position = 'fixed';
        el.style.top = 'auto';
        el.style.bottom = '30px';
      }

      if (isLowerThanParent) {
        el.style.position = 'absolute';
        el.style.top = '0';
        el.style.bottom = 'unset';
      }

      if (isHigherThanParent) {
        el.style.position = 'absolute';
        el.style.top = 'auto';
        el.style.bottom = '0';
      }

      // debouncing at 50ms make it appear lagging when playing with window size.
      // but in real world, people don't resize their window with this menu open
      // for fun. Resizes would occur occasionally, and mostly only on mobile
      // devices (due to orientation change)
    }, 50),
    [containerRef.current, offset],
  );

  // Reason for its existence: https://loopline-systems.atlassian.net/l/cp/1UwCPpuH
  const renderCountRef = useRef(0);
  useEffect(() => {
    renderCountRef.current += 1;
  });

  // Repostion self when re-rendered (https://loopline-systems.atlassian.net/l/cp/1UwCPpuH)
  useEffect(repositionEl, [renderCountRef.current]);

  useEffect(() => {
    window.addEventListener('resize', repositionEl);

    // This don't really do much because menu is *always* rendered, but still a good
    // practice to always remove your event-listeners; gotta watch your back for
    // 'em pesky memory leaks
    return () => {
      window.removeEventListener('resize', repositionEl);
    };
  }, []);

  const onHeaderClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (defaultUrl.indexOf('http') === 0) {
      e.preventDefault();
      e.stopPropagation();
      document.location.assign(defaultUrl);
    }
  };

  const links = typeof sectionLinks === 'function' ? sectionLinks() : sectionLinks;

  const renderItem = (item: SideBarSection) => {
    if (item.link === 'key_result_stream') return <KeyResultSubNav key="key_result_stream" />;

    return <ListItem key={item.link ?? item.url} t={t} item={item} />;
  };

  if(!hasSections && hideMenuIfEmpty) return null;

  return (
    <SubNavContainer $offset={offset} ref={containerRef}>
      <SC.SubNavHeader to={defaultUrl} onClick={onHeaderClick}>
        {(title && t(title)) || name}
      </SC.SubNavHeader>
      <ul>
        {links &&
          links.map((section) => (
            <SC.SubNavSection key={section.id}>{section.items.map(renderItem)}</SC.SubNavSection>
          ))}
      </ul>
    </SubNavContainer>
  );
};

export default ListSubNav;
